<template>
  <v-chart class="chart" ref="chart" :option="option" autoresize ></v-chart>
</template>

<script setup>
import { use } from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
  GridComponent,
  TitleComponent,
  LegendComponent,
  TooltipComponent,
  ToolboxComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  DatasetComponent,
  VisualMapComponent,
} from 'echarts/components';
import VChart, { THEME_KEY } from 'vue-echarts';
import { ref, provide } from 'vue';
import { Lang } from '@/lang/lang';
import { colorList } from '@/config/chartColors';
import { separator } from '@/utils/format.js';


// 注册 ECharts 组件
use([
  LineChart,
  TitleComponent,
  GridComponent,
  TooltipComponent,
  ToolboxComponent,
  LegendComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  CanvasRenderer,
  DatasetComponent,
  VisualMapComponent,
]);

provide(THEME_KEY, 'light');

const props = defineProps(['title', 'dataZoom', 'dataType', 'unit'])
const emit = defineEmits(['refresh']);

const baseXvalues = Array.from({ length: 25 }, (_, i) => (i % 24).toString().padStart(2, '0'));
const baseYvalues = Array(25).fill(0);


const baseOption = {
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      label: {
        backgroundColor: '#2684FC',
        borderColor: '#2684FC'
      }
    },
    zlevel: 999, // 设置 tooltip 的 zlevel
    // z: 999,
    position: function (point, params, dom, rect, size) {
      // 固定在顶部
      const tooltipWidth = size.contentSize[0];
      const chartWidth = size.viewSize[0];

      let xPosition = point[0];
      let yPosition = '20%';  // 固定在顶部

      // 检查是否超出右侧边界
      if (xPosition + tooltipWidth > chartWidth) {
        xPosition = chartWidth - tooltipWidth*2 + 5;
      } else if (xPosition < tooltipWidth) {
        xPosition = tooltipWidth + 5;
      }

      return [xPosition, yPosition];
    },
    backgroundColor: 'rgba(15, 23, 42, 0.9)', // 设置背景颜色为黑色
    textStyle: {
      color: '#fff', // 设置文字颜色为白色
      fontSize: 14
    },
    borderColor: 'transparent', // 设置边框颜色
    borderWidth: 0, // 设置边框宽度
    padding: [10, 15], // 设置内边距
    formatter: function (params) {
      // 获取当前系列的
      const unit = params[0].seriesUnit || props.unit; // 如果没有定义，默认使用全局
      let result = `${params[0].name}<br/>`; // 第一行显示数据名称
      params.forEach(item => {
        let separatorValue = item.value;
        if (separatorValue !== '') {
          separatorValue = separator(item.value, 3)
        } else {
          separatorValue = '-'
        }
        result += `
          <div style="display: flex; justify-content: space-between;">
              <span style="flex: 1;">${item.marker} ${item.seriesName}</span>
              <span style="text-align: right;padding-left: 12px;font-weight: bold;">${separatorValue} <span style="font-weight: normal;">${unit}</span></span>
          </div>
        `;
      });
      return result;
    },
  },
  legend: {
    type: 'scroll',
    top: 10,
    left: 90,
    itemHeight: 8,
    itemWidth: 8,
    icon: 'circle',
    textStyle: {
      fontSize: 12,
      color: '#616D7C' // Set the legend text color to #2684FC
    },
  },
  grid: {
    left: '4%',
    right: '2%',
    top: '20%',
    bottom: '2%',
    containLabel: true
  },
  graphic: [{
    type: 'group',
    left: 20,
    top: 16,
    children: [{
      type: 'text',
      style: {
        text: `${Lang.label.unit}:`,
        fill: '#616D7C',
        fontSize: 12
      },
      x: 0,  // 相对group容器的坐标
      y: 0
    }, {
      type: 'text',
      style: {
        text: props.unit,
        fill: '#616D7C',
        fontSize: 12
      },
      x: 32,  // 通过像素值控制间距
      y: 0
    }, {
      type: 'rect',
      left:  65,
      top: 0,
      shape: {
        width: 1,
        height: 12
      },
      style: {
        fill: '#E7EAEE'
      }
    }]
  }],
  dataZoom: [
    {
      type: 'inside',
      show: true,
      height: 15,
      start: 0.1,
      end: 100
    }
  ],
  axisLabel: {
    color: '#BAC0CC' // Set the y-axis text color to #616D7C
  },
  xAxis: [{
    type: 'category',
    boundaryGap: true,
    data: baseXvalues,
    splitLine: {
      lineStyle: {
        // type: 'dashed' // Set the y-axis grid lines to dashed
      }
    },
    axisLine: {
      lineStyle: {
        color: '#E3E8F2'
      }
    },
  }],
  yAxis: [{
    type: 'value',
    // offset: 0, // 加 y 轴的偏移量，加大柱条与 axisTick 的间距
    splitLine: {
      lineStyle: {
        color: '#E9EEF9',
        // type: 'dashed' // Set the y-axis grid lines to dashed
      }
    },
    axisLine: {
      lineStyle: {
        color: '#E9EEF9'
      }
    },
    axisLabel: {
      color: '#BAC0CC'
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: '#E9EEF9'
      }
    },
  }],
  series: [
    {
      name: '',
      type: 'bar',
      lineStyle: { color: '#2684FC', width: 2 },
      itemStyle: { 
        color: '#2684FC',
        // borderRadius: [10, 10, 0, 0] //圆角位置，左上、右上、右下、左下圆角
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: '#8ae8d7d4' },
            { offset: 1, color: '#ffffff' },
          ],
        },
      },
      emphasis: { focus: 'series' },
      data: baseYvalues,
      unit: props.unit
      // barWidth: 16,
    },
  ],
};

const option = ref({ ...baseOption });

function createSeries(index, colorStops, data, name, unit) {
  const serie = {
    index,
    type: 'bar',
    lineStyle: { color: colorList[index] || colorList[~~(Math.random()*20)], width: 1 },
    stack: index, // 相同时不覆盖
    smooth: false,
    symbolSize: 2,
    itemStyle: {
      // borderRadius: [10, 10, 0, 0], //圆角位置，左上、右上、右下、左下圆角
      color: colorList[index] || colorList[~~(Math.random()*20)] // Set the series color to #2684FC
    },
    legend: {
      itemHeight: 8,
      itemWidth: 8,
      type: 'scroll',
      icon: 'circle',
      textStyle: {
        fontSize: 14,
        color: '#616D7C' // Set the legend text color to #2684FC
      },
    },
    areaStyle: {
      color: {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops,
      },
    },
    emphasis: { focus: 'series' },
    data,
    name,
    unit,
    smooth: true,
  };
  if (props.dataType >= 8) {    
    if (data.length < 32) {
      serie.barWidth = 4;
    } else if (data.length < 64) {
      serie.barWidth = 2;
    } else if (data.length < 128) {
      serie.barWidth = 1;
    }
  }
  return serie;
}

function getXvals(data) {
  let xvals = [];
  data.map(item => {
    if (item.xvals && item.xvals.length) {
      xvals = item.xvals;
    }
  })
  return xvals;
}

function getSeries(data) {
  if (!data || !data.length) {
    return [];
  }
  const nameCount = {};
  return data.map((item, index) => {
    const colorStart = colorList[index] || colorList[~~(Math.random()*20)] || '#2684FC';
    const colorStops = index % 2 === 0
      ? [{ offset: 0, color: colorStart }, { offset: 1, color: '#ffffff' }]
      : [{ offset: 0, color: colorStart }, { offset: 1, color: '#ffffff' }];

      let name = item.dimensionName;
    if (nameCount[name] === undefined) {
      nameCount[name] = 0;
    } else {
      nameCount[name]++;
      name += ` (${nameCount[name]})`;
    }
    return createSeries(
      index,
      colorStops,
      item.yvals || item.linkRelativeVal,
      name,
      item.unit,
    );
  })
}

function updateChart(customData) {
  option.value = { ...baseOption };
  const series = getSeries(customData)
  const xvals = getXvals(customData);
  if (xvals.length) {
    option.value.xAxis[0].data = xvals;
    option.value.yAxis[0].offset = 20;
    option.value.series = series;
  }
}

function refresh() {
  emit('refresh');
}

defineExpose({ updateChart });
</script>

<style scoped>
.chart {
  height: 100vh;
}
</style>